
/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { segEnum, dateEnum, cmpEnum } from "./constants"

/**
 * Parses the raw text of an assistant reply into what the chat view displays
 * @param {string} kind - reply type, 'help' or 'message'
 * @param {string} content - raw reply text
 * @returns {object} text to display, and url, tourSteps and targetTab when the reply has a tour
 */
export const parseAiReply = (kind, content) => {
  const text = content ?? '';
  if (kind === 'message' && text.includes('URL_TAIL==')) {
    return {
      text: `
            \`\`\`
               ${text}
            \`\`\`
            `,
    };
  }
  if (kind === 'help' && text.includes('<STEPS>')) {
    try {
      const stepsText = text.match(/<STEPS[^>]*>([\s\S]*?)<\/STEPS>/i)?.[1]?.trim() || '';
      const targetTabValue = text.match(/<TAB[^>]*>([\s\S]*?)<\/TAB>/i)?.[1]?.trim() || '';
      const urlUi = text.match(/<URL[^>]*>([\s\S]*?)<\/URL>/i)?.[1]?.trim() || '';
      const executableURL = urlUi.split(';');
      const tourSteps = JSON.parse(stepsText);
      return {
        text: [...tourSteps.map((step) => String(step.content)), executableURL[0]]
          .filter(Boolean)
          .join('\n'),
        url: executableURL[1],
        tourSteps,
        targetTab: targetTabValue.split('=')[1],
      };
    } catch (error) {
      return { text };
    }
  }
  return { text };
};

/**
 * Maps a saved chat history record to the message shape used by the chat view
 * @param {object} record - record from the chat history endpoint
 * @returns {object} chat view message
 */
export const toChatMessage = ({ id, role, kind, content }) => {
  const isHuman = ['human', 'user'].includes(String(role).toLowerCase());
  return {
    id,
    role: isHuman ? 'Human' : 'AI',
    ...(isHuman ? { text: content ?? '' } : parseAiReply(kind, content)),
    fromHistory: true,
  };
};

export const extractUrlTailObjects =(sentence)=> sentence.match(/URL_TAIL==([\w\s=&, -]+)/)?.[1] || null

export const  parseQueryString = (queryString) =>Object.fromEntries(queryString.split("&").map(pair => pair.split("=")))

export const  buildQueryString =(map) => new URLSearchParams(map).toString()

export const getKeyByValue =(obj, value) => 
   Object.entries(obj).find(([, val]) => val === value)?.[0] 

export const validateUrlTail =(urlTail) =>{ 
  const errors = [];

  // Check for encoded characters
  if (/%/.test(urlTail)) {
    errors.push("URL tail should not contain encoded characters like '%25'");
  }

  // Extract #section
  const hashIndex = urlTail.indexOf('#');
  let base = urlTail;
  let section = null;

  if (hashIndex === -1) {
    errors.push("URL tail must end with a '#section' part");
  } else {
    base = urlTail.slice(0, hashIndex);
    section = urlTail.slice(hashIndex + 1);

    if (!section || section.includes('%')) {
      errors.push("Section tag after '#' must be a valid string and not URL encoded");
    }
    if (!/^[a-z_]+$/i.test(section)) {
      errors.push(`Invalid characters in section tag: "${section}"`);
    }
  }

  // Split base on '&'
  const parts = base.split('&');
  for (let part of parts) {  // eslint-disable-line
    const [key, val] = part.split('='); // eslint-disable-line

    // General pattern check
    if (!/^[a-z]{3,4}$/.test(key)) {
      errors.push(`Invalid key format: "${key}"`);
    }

    if (val === undefined) {
      errors.push(`Missing value for key: "${key}"`);
      
      continue; // eslint-disable-line
    }

    // Specific field validations
    if (key === 'seg') {
      const match = val.match(/^([a-z]{2})(\d+)$/);
      if (!match || !segEnum.includes(match[1])) {
        errors.push(`Invalid 'seg' value: "${val}". Expected one of [${segEnum.join(', ')}] followed by a number`);
      }
    }

    if (key === 'bok' || key === 'sty') {
      if (!/^(\d{8}-\d{8})$/.test(val) && !dateEnum.includes(val)) {
        errors.push(`Invalid '${key}' value: "${val}". Must be a date range or one of [${dateEnum.join(', ')}]`);
      }
    }

    if (key === 'cmp' && !cmpEnum.includes(val)) {
      errors.push(`Invalid 'cmp' value: "${val}". Expected one of [${cmpEnum.join(', ')}]`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}